import {
  TextField as MuiTextField,
  StandardTextFieldProps,
} from "@mui/material";
import { Box, SxProps } from "@mui/system";
import { ChangeEvent, useEffect, useState } from "react";

import { theme } from "../mui-customs/Theme";
import Typography from "./Typography";

export interface TextFieldProps extends StandardTextFieldProps {
  containerSx?: SxProps;
  initialValue?: string;
  isRequired?: boolean;
  description?: string;
  showInput?: boolean;
  /**
   * @description Used to adapt the onChange event
   * Used to replace spaces with dashes for Vanity Text
   */
  adaptOnChange?: (
    event: ChangeEvent<HTMLInputElement>,
    setLocalValue: (value: string) => void
  ) => void;
}

const TextField = ({
  sx,
  label,
  type = "text",
  isRequired,
  showInput = true,
  value,
  initialValue,
  containerSx,
  description,
  onChange,
  adaptOnChange,
  ...props
}: TextFieldProps) => {
  const [localValue, setLocalValue] = useState(initialValue);
  useEffect(() => {
    typeof value === "string" && setLocalValue(value);
  }, [value]);

  return (
    <Box
      sx={{
        width: { xs: "100%" },
        mx: { xs: "none" },
        ...containerSx,
      }}
    >
      <Typography variant="body2">
        {label}
        {isRequired ? (
          <Box component="span" sx={{ color: theme.red[500] }}>
            *
          </Box>
        ) : (
          <></>
        )}
      </Typography>
      {description && (
        <Typography
          variant="verySmall2"
          sx={{
            color: theme.black[500],
            fontStyle: "italic !important",
            my: 2,
          }}
        >
          {description}
        </Typography>
      )}
      {showInput && (
        <MuiTextField
          onWheel={(event) => (event.target as HTMLElement).blur()}
          {...props}
          sx={{
            width: { xs: "100%" },
            fontWeight: "400",
            fontSize: "14px",
            lineHeight: "24px",
            color: theme.black[800],
            mt: 1,
            "& .MuiInputBase-root": {
              fontFamily: "Poppins",
              background: theme.whiteBg.WBG3,
              borderWidth: "0",
              borderRadius: "6px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: "0px",
            },
            "::placeholder": {
              fontWeight: "400",
              color: theme.grey2[400],
            },
            ...sx,
          }}
          value={localValue ?? ""}
          type={type}
          onChange={(event: any) => {
            if (type === "number" && event.target.value < 0) {
              return;
            }
            setLocalValue(event.target.value);
            onChange && onChange(event);
            // replace space with "-"
            adaptOnChange && adaptOnChange(event, setLocalValue);
          }}
        />
      )}
    </Box>
  );
};

export default TextField;
